//
// # Netlist Types
//
// Runtime-only netlist representation computed on-demand from schematics.
// Not persisted - recomputed each time from schematic connectivity.
// Used for:
// - Auto-populating layout conns for routing
// - Future LVS comparisons
// - Future parasitic annotation
//

import { schema } from "GenAlphaSchema";

// Computed netlist - runtime only, not persisted
export interface ComputedNetlist {
  // Reference to the source schematic
  sourceSchematic: schema.Ident;

  // Core connectivity data
  nets: Array<Net>;
  instances: Array<NetlistInstance>;
  ports: Array<NetlistPort>;
}

// A net represents a signal/wire connecting multiple ports
export interface Net {
  // Net name (signal name from schematic, e.g., "vdd", "net0", "data<7:0>")
  name: string;

  // Width in bits (1 for scalar, >1 for buses)
  width: number;

  // Signal usage hint (optional)
  usage?: NetUsage;

  // All connections to this net
  connections: Array<NetConnection>;
}

// Signal usage classification
export type NetUsage = "signal" | "power" | "ground" | "clock" | "io";

// A connection to a net: either an instance port or a module port
export type NetConnection = InstancePortConnection | ModulePortConnection;

// Connection to an instance's port
export interface InstancePortConnection {
  kind: "inst-port";
  inst: string; // Instance name
  port: string; // Port name on the instance
  index?: number; // For bus ports, which bit (optional)
}

// Connection to a top-level module port (schematic pin)
export interface ModulePortConnection {
  kind: "module-port";
  port: string; // Port name
  index?: number; // For bus ports, which bit (optional)
}

// Instance in the netlist
export interface NetlistInstance {
  name: string; // Instance name (e.g., "M1", "R1[0:3]")
  target: schema.Ident; // What this instance instantiates
}

// Top-level port in the netlist (corresponds to schematic pin)
export interface NetlistPort {
  name: string; // Port name
  direction: schema.PortDir; // Input/Output/Inout
  width: number; // Width in bits
  connectedNet?: string; // Which net this port connects to (if any)
}

// Result of netlist extraction with warnings
export interface NetlistExtractionResult {
  netlist: ComputedNetlist;
  warnings: Array<NetlistWarning>;
}

// Warning during netlist extraction
export interface NetlistWarning {
  kind: NetlistWarningKind;
  message: string;
  details?: unknown;
}

export type NetlistWarningKind =
  | "unconnected-instance-port" // Instance port not connected to any net
  | "unconnected-module-port" // Module port not connected to any net
  | "width-mismatch" // Width mismatch between port and signal
  | "duplicate-net-name"; // Multiple signals with the same name

// Helper to infer net usage from name
export function inferNetUsage(name: string): NetUsage | undefined {
  const lower = name.toLowerCase();

  // Power/ground detection
  if (
    lower === "vdd" ||
    lower === "vcc" ||
    lower === "vss" ||
    lower.startsWith("vdd") ||
    lower.startsWith("vcc")
  ) {
    return "power";
  }
  if (
    lower === "gnd" ||
    lower === "vss" ||
    lower === "ground" ||
    lower.startsWith("gnd") ||
    lower.startsWith("vss")
  ) {
    return "ground";
  }

  // Clock detection
  if (
    lower === "clk" ||
    lower === "clock" ||
    lower.startsWith("clk") ||
    lower.endsWith("_clk")
  ) {
    return "clock";
  }

  // Default to signal
  return "signal";
}
